import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Check, ArrowLeft, Sparkles } from "lucide-react";
import { BrandWordmark } from "@/components/BrandWordmark";
import { PLANS } from "@/lib/plans";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";
import { CreateathonBanner } from "@/components/CreateathonBanner";
import { Helmet } from "react-helmet-async";

const PromoBanner = () => {
  const [promo, setPromo] = useState(null);
  useEffect(() => {
    api.get("/promo").then((r) => r.data.active && r.data.text && setPromo(r.data.text)).catch(() => {});
  }, []);
  if (!promo) return null;
  return (
    <div className="mb-10 border border-emerald-500/40 rounded-md bg-emerald-600/10 px-5 py-3.5 flex items-center gap-3" data-testid="promo-banner">
      <Sparkles className="h-4 w-4 text-emerald-400 shrink-0" />
      <p className="text-sm">
        <span className="font-semibold text-emerald-300">This month's special:</span>{" "}
        <span className="text-zinc-200">{promo}</span>
      </p>
    </div>
  );
};

const FAQ = [
  {
    q: "Is the free trial really free?",
    a: "Yes — sign up with just your email and you get enough credits to make a couple of full short videos end-to-end. No card required.",
  },
  {
    q: "What's a credit?",
    a: "Credits power AI generation — scripts, studio voiceovers and cinematic scene visuals. Every paid plan includes a monthly credit allotment.",
  },
  {
    q: "Can I change or cancel my plan?",
    a: "Plans are month-to-month. Upgrade, downgrade, or cancel anytime — you keep any credits already in your balance.",
  },
  {
    q: "Does Videoileo post to social automatically?",
    a: "You can schedule a whole series to a YouTube, TikTok & Instagram queue today. Fully automated publishing to those platforms is rolling out.",
  },
  {
    q: "How do I change or reset my password?",
    a: "Logged in? Head to Settings → Change password. Forgot it? Use the \"Forgot password?\" link on the sign-in page and we'll email you a secure reset link that expires in 60 minutes. Google sign-in users can use the same link to set a password too.",
  },
  {
    q: "Will I be notified if my password changes?",
    a: "Yes — every password change triggers an instant security alert email to your account address, so you can react immediately if it wasn't you.",
  },
];

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="pricing-public-page">
      <ExitIntentPopup />
      <CreateathonBanner />
      <Seo
        title="Pricing & Plans"
        description="Start free with just your email, then scale with Videoileo plans from $19/mo. Credits power AI scripts, studio voiceovers and cinematic visuals for your faceless video channel."
        path="/pricing"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
      </Helmet>
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandWordmark className="h-12" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-zinc-300 hover:text-white px-3 py-2 transition-colors">Sign in</Link>
            <Link to="/register" data-testid="pricing-start-free" className="text-sm bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(0,122,255,0.3)]">
              Start free
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-16">
        <PromoBanner />
        <Link to="/" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5 mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Plans &amp; pricing</p>
        <h1 className="font-head text-4xl md:text-6xl tracking-tighter font-black mb-4">Start free. Scale when it works.</h1>
        <p className="text-zinc-400 max-w-xl mb-5 leading-relaxed">
          Try it free with just your email — make a couple of full short videos, then upgrade for monthly credits.
          Limited-time launch pricing.
        </p>
        <div className="border border-white/10 rounded-md bg-[#121212] inline-block px-4 py-2 text-sm text-zinc-400 mb-12">
          <span className="text-zinc-300 font-medium">Every plan includes monthly credits</span> that power AI scripts, studio voiceovers &amp; cinematic visuals.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              data-testid={`pricing-plan-${plan.id}`}
              className={`relative border rounded-md p-6 flex flex-col ${plan.popular ? "border-blue-500 bg-blue-600/10" : "border-white/10 bg-[#121212]"}`}
            >
              {plan.popular && (
                <span className="absolute -top-2.5 left-6 text-[10px] uppercase tracking-wider bg-blue-600 px-2 py-0.5 rounded-full font-semibold">
                  Most popular
                </span>
              )}
              <div className="flex items-center gap-2 mb-1">
                <plan.icon className="h-4 w-4 text-blue-400" />
                <p className="font-head text-lg font-semibold">{plan.name}</p>
              </div>
              <p className="text-xs text-zinc-500 mb-4 min-h-[32px]">{plan.tagline}</p>
              <div className="flex items-end gap-2 mb-5">
                <span className="font-head text-4xl font-black">{plan.price === 0 ? "$0" : `$${plan.price}`}</span>
                {plan.price !== 0 && <span className="text-zinc-500 mb-1 text-sm">/ mo</span>}
                {plan.oldPrice && <span className="text-sm text-zinc-600 line-through mb-1.5">${plan.oldPrice}</span>}
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {plan.features.map((f, i) => (
                  <li key={f} className="text-sm text-zinc-400 flex items-start gap-2">
                    <Check className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                data-testid={`pricing-cta-${plan.id}`}
                className={`w-full text-center py-2.5 rounded-md font-semibold transition-colors ${plan.popular ? "bg-blue-600 hover:bg-blue-500" : "bg-white/5 border border-white/10 hover:bg-white/10"}`}
              >
                {plan.price === 0 ? "Start free" : plan.cta}
              </Link>
            </div>
          ))}
        </div>

        <p className="text-xs text-zinc-600 mt-6">Demo checkout — subscribing adds the plan's monthly credits instantly without a real payment.</p>

        {/* FAQ */}
        <div className="mt-20 max-w-3xl">
          <h2 className="font-head text-2xl md:text-3xl font-bold tracking-tight mb-8">Questions</h2>
          <div className="space-y-4">
            {FAQ.map((item) => (
              <div key={item.q} className="border border-white/10 rounded-md bg-[#121212] p-5">
                <h3 className="font-head font-semibold mb-1.5">{item.q}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-zinc-500 mt-8 leading-relaxed" data-testid="pricing-guides-links">
            <Link to="/faq" data-testid="pricing-all-faq-link" className="text-blue-400 hover:text-blue-300 underline">See all questions on the FAQ page →</Link>
            <br /><br />
            New to faceless channels? Read our free guides:{" "}
            <Link to="/guides/how-to-start-a-faceless-youtube-channel" className="text-blue-400 hover:text-blue-300 underline">how to start a faceless YouTube channel</Link>,{" "}
            <Link to="/guides/best-faceless-video-niches" className="text-blue-400 hover:text-blue-300 underline">the best faceless niches</Link> and{" "}
            <Link to="/guides/youtube-automation-guide" className="text-blue-400 hover:text-blue-300 underline">how YouTube automation works</Link>.
            {" "}Questions? <Link to="/contact" className="text-blue-400 hover:text-blue-300 underline">Contact us</Link>.
          </p>
        </div>
      </main>

      <footer className="border-t border-white/10 py-10 px-5 text-center text-sm text-zinc-600">
        © 2026 Videoileo · The faceless video studio.{" "}
        <Link to="/" className="text-zinc-500 hover:text-zinc-300 underline ml-2">Home</Link>
        <Link to="/guides" className="text-zinc-500 hover:text-zinc-300 underline ml-3">Guides</Link>
      </footer>
    </div>
  );
}
